import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, authHeaders } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wallet, Loader2, TicketCheck, Check, Clock, X, ChevronDown, ChevronUp, CalendarDays, Paperclip } from "lucide-react";
import { euro, relativeDate } from "@/lib/format";
import { fileToDataUrl } from "@/lib/image";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function History() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amounts, setAmounts] = useState({});
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [openGroups, setOpenGroups] = useState({});
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReceipt, setRejectReceipt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/vendor/history", { headers: authHeaders("vendor") });
      setData(r.data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submitAmount = async (claimId) => {
    const rec = amounts[claimId] || {};
    const amount_spent = Number(rec.spent);
    const discount_eur = Number(rec.disc || 0);
    if (!amount_spent || amount_spent <= 0) return;
    try {
      await api.post(`/vendor/claims/${claimId}/amount`, { amount_spent, discount_eur }, { headers: authHeaders("vendor") });
      toast.success("Importo registrato");
      setAmounts((a) => ({ ...a, [claimId]: {} }));
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const respond = async (claimId, action, receipt) => {
    try {
      await api.post(`/vendor/claims/${claimId}/amount/respond`, { action, receipt: receipt || undefined }, { headers: authHeaders("vendor") });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Errore"); }
  };

  const pickRejectReceipt = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { setRejectReceipt(await fileToDataUrl(f, 1400, 0.8)); }
    catch (_) { toast.error("Impossibile caricare l'immagine"); }
    e.target.value = "";
  };

  const doReject = async () => {
    const id = rejectTarget; const rec = rejectReceipt;
    setRejectTarget(null); setRejectReceipt(null);
    await respond(id, "ignore", rec);
    toast.success("Importo rifiutato. Deciderà un amministratore.");
  };

  const items = data?.items || [];
  const filtered = useMemo(() => {
    const f = from ? new Date(from + "T00:00:00") : null;
    const t = to ? new Date(to + "T23:59:59") : null;
    return items.filter((it) => {
      if (!it.date) return true;
      const d = new Date(it.date);
      if (f && d < f) return false;
      if (t && d > t) return false;
      return true;
    });
  }, [items, from, to]);

  const groups = useMemo(() => {
    const map = {};
    filtered.forEach((it) => {
      const key = (it.title || "—").trim().toLowerCase();
      if (!map[key]) map[key] = { key, title: it.title || "Coupon", count: 0, total: 0, pending: 0, latest: it.date, items: [] };
      const g = map[key];
      g.items.push(it);
      g.count += 1;
      if (it.amount_spent != null) g.total += Number(it.amount_spent);
      if (it.amount_spent == null || it.amount_status === "pending") g.pending += 1;
      if (it.date && (!g.latest || new Date(it.date) > new Date(g.latest))) g.latest = it.date;
    });
    return Object.values(map).sort((a, b) => new Date(b.latest || 0) - new Date(a.latest || 0));
  }, [filtered]);

  const periodRevenue = useMemo(() => filtered.filter((it) => it.amount_status === "confirmed").reduce((a, it) => a + Number(it.amount_spent || 0), 0), [filtered]);
  const periodDiscount = useMemo(() => filtered.filter((it) => it.amount_status === "confirmed").reduce((a, it) => a + Number(it.discount_eur || 0), 0), [filtered]);
  const filterActive = !!(from || to);

  const isOpen = (g) => (openGroups[g.key] !== undefined ? openGroups[g.key] : g.pending > 0);
  const toggle = (k) => setOpenGroups((p) => ({ ...p, [k]: !(p[k] !== undefined ? p[k] : false) }));

  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#E63946]" /></div>;

  const renderItem = (it) => (
    <div key={it.claim_id} className="kn-card p-3.5" data-testid={`history-${it.claim_id}`}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0"><TicketCheck className="h-5 w-5 text-emerald-600" /></div>
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-800 truncate">{it.title}</p>
          <p className="text-xs text-slate-400 truncate">{it.client_name || "Cliente"} · {relativeDate(it.date)}</p>
        </div>
        {it.amount_spent != null ? (
          <div className="text-right shrink-0">
            <p className="font-extrabold text-slate-900">{euro(it.amount_spent)}</p>
            <span className={`text-[11px] font-bold ${it.amount_status === "confirmed" ? "text-emerald-600" : it.amount_status === "disputed" ? "text-[#DC2626]" : "text-amber-600"}`}>
              {it.amount_status === "confirmed" ? "Confermato" : it.amount_status === "disputed" ? "In verifica admin" : "In attesa"}
            </span>
          </div>
        ) : (
          <span className="text-xs text-slate-400 shrink-0 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Da inserire</span>
        )}
      </div>

      {it.amount_spent == null && (
        <div className="flex gap-2 mt-3">
          <Input type="number" min="0" placeholder="Speso €" value={(amounts[it.claim_id] || {}).spent || ""}
            onChange={(e) => setAmounts((a) => ({ ...a, [it.claim_id]: { ...(a[it.claim_id] || {}), spent: e.target.value } }))}
            className="h-10 rounded-xl" data-testid={`amount-input-${it.claim_id}`} />
          <Input type="number" min="0" placeholder="Sconto €" value={(amounts[it.claim_id] || {}).disc || ""}
            onChange={(e) => setAmounts((a) => ({ ...a, [it.claim_id]: { ...(a[it.claim_id] || {}), disc: e.target.value } }))}
            className="h-10 rounded-xl" data-testid={`discount-input-${it.claim_id}`} />
          <Button onClick={() => submitAmount(it.claim_id)} className="h-10 rounded-xl font-bold shrink-0" data-testid={`amount-save-${it.claim_id}`}>Registra</Button>
        </div>
      )}
      {it.amount_status === "pending" && it.amount_by === "client" && (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-slate-500">Il cliente ha indicato <b>{euro(it.amount_spent)}</b>{it.vendor_amount_spent != null && Number(it.vendor_amount_spent) !== Number(it.amount_spent) ? <> (tu avevi messo {euro(it.vendor_amount_spent)})</> : null}. Accetta o rifiuta.</p>
          <div className="flex gap-2">
            <Button onClick={() => respond(it.claim_id, "confirm")} className="flex-1 h-10 rounded-xl font-bold gap-1.5" data-testid={`amount-accept-${it.claim_id}`}><Check className="h-4 w-4" /> Accetta {euro(it.amount_spent)}</Button>
            <Button variant="outline" onClick={() => { setRejectReceipt(null); setRejectTarget(it.claim_id); }} className="flex-1 h-10 rounded-xl font-bold gap-1.5 text-[#DC2626] border-[#DC2626]/30" data-testid={`amount-reject-${it.claim_id}`}><X className="h-4 w-4" /> Rifiuta</Button>
          </div>
        </div>
      )}
      {it.amount_status === "disputed" && (
        <div className="mt-3 p-2.5 rounded-xl bg-red-50 text-[#DC2626] text-xs font-semibold" data-testid={`amount-disputed-${it.claim_id}`}>
          Importo contestato. Un amministratore verificherà e deciderà l'importo finale.
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-5 kn-fade-up">
      <div><h1 className="text-2xl font-extrabold text-slate-900">Incassi</h1><p className="text-slate-500">Storico operazioni e ricavi generati da KoupoNow.</p></div>

      <div className="kn-gradient rounded-2xl p-5 text-white kn-shadow-soft">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-white/20 flex items-center justify-center"><Wallet className="h-6 w-6" /></div>
          <div><p className="text-white/80 text-sm font-semibold">{filterActive ? "Incassato nel periodo" : "Incassato totale"}</p><p className="text-3xl font-extrabold">{euro(filterActive ? periodRevenue : data?.total_revenue)}</p></div>
        </div>
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="bg-white/15 rounded-xl p-3"><p className="text-white/75 text-xs">Sconti erogati</p><p className="font-extrabold text-lg">{euro(filterActive ? periodDiscount : data?.total_discounted)}</p></div>
          <div className="bg-white/15 rounded-xl p-3"><p className="text-white/75 text-xs">Operazioni</p><p className="font-extrabold text-lg">{filterActive ? filtered.length : (data?.operations || 0)}</p></div>
        </div>
      </div>

      {/* Date filters */}
      <div className="kn-card p-3 flex flex-wrap items-end gap-3" data-testid="history-date-filter">
        <div className="flex items-center gap-1.5 text-slate-500 text-sm font-bold"><CalendarDays className="h-4 w-4" /> Periodo</div>
        <div className="flex-1 min-w-[130px]">
          <label className="text-xs text-slate-400 block mb-1">Dal</label>
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-10 rounded-xl" data-testid="history-date-from" />
        </div>
        <div className="flex-1 min-w-[130px]">
          <label className="text-xs text-slate-400 block mb-1">Al</label>
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="h-10 rounded-xl" data-testid="history-date-to" />
        </div>
        {filterActive && (
          <Button variant="outline" onClick={() => { setFrom(""); setTo(""); }} className="h-10 rounded-xl font-bold" data-testid="history-date-reset">Azzera</Button>
        )}
      </div>

      {data?.pending_confirm > 0 && (
        <div className="p-3 rounded-xl bg-amber-50 text-amber-700 text-sm font-semibold">{data.pending_confirm} importi in attesa di conferma dal cliente.</div>
      )}

      {/* Grouped by coupon */}
      <div className="space-y-2">
        {groups.length === 0 && <div className="kn-card p-10 text-center text-slate-400">Nessuna operazione nel periodo.</div>}
        {groups.map((g) => {
          const open = isOpen(g);
          return (
            <div key={g.key} data-testid={`history-group-${g.key}`}>
              <button onClick={() => toggle(g.key)} data-testid={`history-group-toggle-${g.key}`}
                className="kn-card p-3.5 w-full flex items-center gap-3 text-left hover:ring-1 hover:ring-[#E63946]/30 transition-shadow">
                <div className="h-10 w-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0"><TicketCheck className="h-5 w-5 text-emerald-600" /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-800 truncate">{g.title}</p>
                  <p className="text-xs text-slate-400 truncate">{g.count} {g.count === 1 ? "operazione" : "operazioni"}{g.pending > 0 ? ` · ${g.pending} da gestire` : ""}</p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-extrabold text-slate-900">{euro(g.total)}</p>
                  <span className="text-[11px] text-slate-400">totale</span>
                </div>
                {open ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
              </button>
              {open && (
                <div className="mt-2 ml-3 pl-3 border-l-2 border-slate-100 space-y-2" data-testid={`history-group-detail-${g.key}`}>
                  {g.items.map((it) => renderItem(it))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={!!rejectTarget} onOpenChange={(o) => { if (!o) { setRejectTarget(null); setRejectReceipt(null); } }}>
        <DialogContent className="sm:max-w-md rounded-3xl" data-testid="vendor-reject-dialog">
          <DialogHeader><DialogTitle className="text-xl font-extrabold">Rifiuta l'importo del cliente</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">Se l'importo indicato dal cliente non è corretto puoi rifiutarlo. Deciderà un amministratore.</p>
          <div className="mt-2">
            <label className="text-sm font-semibold text-slate-600">Allega scontrino o fattura <span className="font-normal text-slate-400">(consigliato)</span></label>
            {rejectReceipt ? (
              <div className="mt-2 relative inline-block">
                <img src={rejectReceipt} alt="scontrino" className="h-28 w-28 rounded-xl object-cover border border-slate-200" data-testid="vendor-reject-receipt-preview" />
                <button type="button" onClick={() => setRejectReceipt(null)} data-testid="vendor-reject-receipt-remove"
                  className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-slate-900 text-white flex items-center justify-center shadow"><X className="h-3.5 w-3.5" /></button>
              </div>
            ) : (
              <label data-testid="vendor-reject-receipt-btn"
                className="mt-2 flex items-center justify-center gap-2 h-12 rounded-xl border-2 border-dashed border-slate-300 text-slate-600 font-semibold text-sm cursor-pointer hover:border-[#E63946] hover:text-[#E63946] transition-colors">
                <Paperclip className="h-4 w-4" /> Carica foto dello scontrino
                <input type="file" accept="image/*" capture="environment" className="hidden" onChange={pickRejectReceipt} />
              </label>
            )}
            <p className="text-[11px] text-slate-400 mt-1.5">La foto verrà inviata all'amministratore per decidere l'importo corretto.</p>
          </div>
          <div className="flex gap-2 mt-3">
            <Button variant="outline" onClick={() => { setRejectTarget(null); setRejectReceipt(null); }} className="flex-1 h-11 rounded-xl font-bold">Annulla</Button>
            <Button onClick={doReject} data-testid="vendor-reject-confirm" className="flex-1 h-11 rounded-xl font-bold gap-1.5 bg-[#DC2626] hover:bg-[#B91C1C]"><X className="h-4 w-4" /> Rifiuta importo</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
